import React, { useState } from 'react';
import { db } from '../firebase/config';
import { doc, updateDoc } from 'firebase/firestore';
import {
  Box, Typography, Button, IconButton, Chip, Collapse,
  TextField, Autocomplete, Alert
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

function calcFireDate(deadline, daysBeforeDeadline) {
  if (!deadline) return null;
  try {
    const d = new Date(deadline.replace(' ', 'T'));
    if (isNaN(d.getTime())) return null;
    d.setDate(d.getDate() - Number(daysBeforeDeadline));
    return d.toLocaleDateString('sv-SE');
  } catch {
    return null;
  }
}

export default function TenderAlarms({
  tender, phase, phaseIdx, allUsers, onUpdate,
  collapsed = false, onToggleCollapse
}) {
  const [adding, setAdding] = useState(false);
  const [newDays, setNewDays] = useState(7);
  const [newRecipients, setNewRecipients] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const allAlarms = tender.alarms || [];
  const alarms = allAlarms.filter(a => (a.phaseIdx ?? 0) === phaseIdx);
  const deadline = phase?.submissionDeadline || tender.submissionDeadline;

  const userOptions = allUsers
    .filter(u => u.email)
    .map(u => ({ email: u.email, label: u.name || u.displayName || u.email }));

  async function handleAddAlarm() {
    if (!newDays || newRecipients.length === 0) return;
    setSaving(true);
    setError('');
    try {
      const newAlarm = {
        id: 'alarm_' + Date.now(),
        daysBeforeDeadline: Number(newDays),
        recipients: newRecipients.map(r => typeof r === 'string' ? r : r.email),
        phaseIdx,
        createdAt: new Date().toISOString(),
        lastFiredForDeadline: null,
      };
      const updatedAlarms = [...allAlarms, newAlarm];
      await updateDoc(doc(db, 'tenders', tender.id), { alarms: updatedAlarms });
      onUpdate({ ...tender, alarms: updatedAlarms });
      setAdding(false);
      setNewDays(7);
      setNewRecipients([]);
    } catch (err) {
      console.error(err);
      setError('Kunde inte spara larmet.');
    } finally {
      setSaving(false);
    }
  }

  async function handleRemoveAlarm(alarmId) {
    const updatedAlarms = allAlarms.filter(a => a.id !== alarmId);
    await updateDoc(doc(db, 'tenders', tender.id), { alarms: updatedAlarms });
    onUpdate({ ...tender, alarms: updatedAlarms });
  }

  const firedKey = (alarm) => `${deadline}|${alarm.daysBeforeDeadline}`;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box onClick={onToggleCollapse} sx={{
          display: 'flex', alignItems: 'center', gap: 1,
          cursor: 'pointer', userSelect: 'none', flex: 1,
          '&:hover': { opacity: 0.75 }
        }}>
          <ExpandMoreIcon sx={{
            fontSize: 14, color: '#e65100', flexShrink: 0,
            transform: collapsed ? 'rotate(0deg)' : 'rotate(180deg)',
            transition: 'transform 0.2s'
          }} />
          <NotificationsIcon sx={{ fontSize: 16, color: '#e65100' }} />
          <Typography variant="caption" fontWeight="bold" sx={{ color: '#e65100' }}>
            E-POSTLARM
          </Typography>
          {alarms.length > 0 && (
            <Chip label={`${alarms.length} st`} size="small" sx={{
              height: 16, fontSize: '0.62rem',
              bgcolor: 'rgba(230,81,0,0.12)', color: '#e65100'
            }} />
          )}
        </Box>
        {!collapsed && (
          <Button size="small" variant="outlined"
            startIcon={adding ? null : <AddIcon sx={{ fontSize: 13 }} />}
            onClick={() => { setAdding(a => !a); setError(''); }}
            sx={{
              fontSize: '0.65rem', py: 0.2, px: 1,
              borderColor: '#e65100', color: '#e65100',
              '&:hover': { borderColor: '#bf360c', bgcolor: 'rgba(230,81,0,0.06)' }
            }}>
            {adding ? 'Avbryt' : 'Lägg till larm'}
          </Button>
        )}
      </Box>

      <Collapse in={!collapsed}>
        {!deadline && (
          <Box sx={{ py: 1, px: 1, bgcolor: 'action.hover', borderRadius: 1, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              Sätt en inlämningsdeadline för fasen för att aktivera e-postlarm
            </Typography>
          </Box>
        )}

        {deadline && alarms.length === 0 && !adding && (
          <Box sx={{ py: 1, px: 1, bgcolor: 'action.hover', borderRadius: 1, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              Inga larm inställda — klicka "Lägg till larm" för att skapa ett
            </Typography>
          </Box>
        )}

        {alarms.map(alarm => {
          const fireDate = calcFireDate(deadline, alarm.daysBeforeDeadline);
          const hasFired = alarm.lastFiredForDeadline === firedKey(alarm);
          return (
            <Box key={alarm.id} sx={{
              display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.75,
              p: 1, borderRadius: 1, border: '1px solid', borderColor: 'divider',
              bgcolor: hasFired ? 'rgba(46,125,50,0.06)' : 'action.hover'
            }}>
              <NotificationsIcon sx={{ fontSize: 14, color: hasFired ? '#2e7d32' : '#e65100', mt: 0.3, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                  <Typography variant="caption" fontWeight={600}>
                    {alarm.daysBeforeDeadline} dagar innan deadline
                  </Typography>
                  {fireDate && (
                    <Typography variant="caption" color="text.secondary">
                      · skickas {fireDate}
                    </Typography>
                  )}
                  {hasFired && (
                    <Chip
                      icon={<CheckCircleIcon sx={{ fontSize: '10px !important' }} />}
                      label="Skickat"
                      size="small"
                      sx={{ height: 15, fontSize: '0.6rem', bgcolor: 'rgba(46,125,50,0.15)', color: '#2e7d32' }}
                    />
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary"
                  sx={{ display: 'block', mt: 0.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {alarm.recipients.join(', ')}
                </Typography>
              </Box>
              <IconButton size="small" onClick={() => handleRemoveAlarm(alarm.id)} sx={{ p: 0.3, flexShrink: 0 }}>
                <DeleteIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
              </IconButton>
            </Box>
          );
        })}

        {adding && (
          <Box sx={{
            p: 1.5, border: '1px solid', borderColor: '#e65100',
            borderRadius: 1, bgcolor: 'rgba(230,81,0,0.04)', mt: 0.5
          }}>
            {error && <Alert severity="error" sx={{ mb: 1, py: 0.3, fontSize: '0.8rem' }}>{error}</Alert>}
            <TextField
              label="Dagar innan deadline"
              type="number"
              size="small"
              value={newDays}
              onChange={e => setNewDays(e.target.value)}
              inputProps={{ min: 1, max: 90 }}
              sx={{ mb: 1.5, width: 190, '& .MuiInputBase-input': { fontSize: '0.85rem' } }}
            />
            <Autocomplete
              multiple freeSolo
              size="small"
              options={userOptions}
              value={newRecipients}
              onChange={(_, v) => setNewRecipients(v)}
              getOptionLabel={o => typeof o === 'string' ? o : o.label}
              isOptionEqualToValue={(o, v) => {
                const oe = typeof o === 'string' ? o : o.email;
                const ve = typeof v === 'string' ? v : v.email;
                return oe === ve;
              }}
              renderInput={params => (
                <TextField {...params} label="Mottagare"
                  placeholder={newRecipients.length === 0 ? 'Välj användare eller skriv e-post...' : ''}
                  sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem' } }}
                />
              )}
              sx={{ mb: 1.5 }}
            />
            <Button size="small" variant="contained"
              disabled={saving || !newDays || newRecipients.length === 0}
              onClick={handleAddAlarm}
              sx={{ bgcolor: '#e65100', '&:hover': { bgcolor: '#bf360c' }, fontSize: '0.78rem' }}>
              {saving ? 'Sparar...' : 'Spara larm'}
            </Button>
          </Box>
        )}
      </Collapse>
    </Box>
  );
}
